import { useEffect, useRef, useState } from "react";
import { HeroInlineLogo } from "@/components/dataTable";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import type { AbsenceDayMarker } from "@/lib/employeeAbsences";
import { formatHours } from "@/lib/employeeTimes";
import {
  invalidateCaptureCalendarCache,
  loadCaptureCalendarHero,
  loadCaptureCalendarMonth,
} from "@/pages/admin/zeiterfassung/captureCalendarCache";
import { YearOverviewCell } from "@/pages/admin/zeiterfassung/YearOverviewCell";
import {
  buildMonthGrid,
  MONTH_LONG,
  todayIso,
  WEEKDAY_SHORT,
  type YearGridCell,
  type YearMonthGrid,
} from "@/pages/admin/zeiterfassung/yearOverviewUtils";
import "./yearOverview.css";

interface CaptureMonthCalendarProps {
  userLogin: string;
  selectedDate: string;
  onSelectDate: (iso: string) => void;
  /** Erhöhen nach Speichern, damit die Stunden aktualisiert werden. */
  refreshKey?: number;
}

function parseIsoParts(iso: string): { year: number; month: number } {
  const [y, m] = iso.slice(0, 10).split("-").map(Number);
  return { year: y || new Date().getFullYear(), month: m || 1 };
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function hoursMapFromSummary(
  days: Array<{ work_date: string; total_hours: number }>,
): Map<number, number> {
  const hoursByDay = new Map<number, number>();
  for (const day of days) {
    const dayNum = Number(day.work_date.slice(8, 10));
    if (Number.isFinite(dayNum)) {
      hoursByDay.set(dayNum, Number(day.total_hours || 0));
    }
  }
  return hoursByDay;
}

function pendingMapFromSummary(
  days: Array<{ work_date: string; pending_justification?: boolean }>,
): Map<number, boolean> {
  const pendingByDay = new Map<number, boolean>();
  for (const day of days) {
    if (!day.pending_justification) continue;
    const dayNum = Number(day.work_date.slice(8, 10));
    if (Number.isFinite(dayNum)) pendingByDay.set(dayNum, true);
  }
  return pendingByDay;
}

export function CaptureMonthCalendar({
  userLogin,
  selectedDate,
  onSelectDate,
  refreshKey = 0,
}: CaptureMonthCalendarProps) {
  const selectedParts = parseIsoParts(selectedDate);
  const [viewYear, setViewYear] = useState(selectedParts.year);
  const [viewMonth, setViewMonth] = useState(selectedParts.month);
  const [monthGrid, setMonthGrid] = useState<YearMonthGrid | null>(null);
  const [heroReady, setHeroReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingJustificationCount, setPendingJustificationCount] = useState(0);
  const requestIdRef = useRef(0);
  const appHoursRef = useRef<Map<number, number>>(new Map());
  const pendingByDayRef = useRef<Map<number, boolean>>(new Map());
  const calendarByDayRef = useRef<Map<string, import("@/lib/employeeCalendar").EmployeeCalendarDay>>(
    new Map(),
  );
  const absenceByDayRef = useRef<Map<string, AbsenceDayMarker[]>>(new Map());

  useEffect(() => {
    const parts = parseIsoParts(selectedDate);
    setViewYear(parts.year);
    setViewMonth(parts.month);
  }, [selectedDate]);

  useEffect(() => {
    if (refreshKey > 0 && userLogin.trim()) {
      invalidateCaptureCalendarCache(userLogin);
    }
  }, [refreshKey, userLogin]);

  useEffect(() => {
    const login = userLogin.trim();
    if (!login) {
      setMonthGrid(null);
      setHeroReady(false);
      setLoading(false);
      setPendingJustificationCount(0);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setHeroReady(false);

    void (async () => {
      try {
        const { summary, calendarByDay, absenceByDay } = await loadCaptureCalendarMonth({
          userLogin: login,
          year: viewYear,
          month: viewMonth,
        });
        if (requestId !== requestIdRef.current) return;

        const hoursByDay = hoursMapFromSummary(summary.days);
        const pendingByDay = pendingMapFromSummary(summary.days);
        appHoursRef.current = hoursByDay;
        pendingByDayRef.current = pendingByDay;
        setPendingJustificationCount(pendingByDay.size);
        calendarByDayRef.current = calendarByDay;
        absenceByDayRef.current = absenceByDay;

        setMonthGrid(
          buildMonthGrid(
            viewYear,
            viewMonth,
            hoursByDay,
            absenceByDay,
            calendarByDay,
            undefined,
            undefined,
            undefined,
            pendingByDay,
          ),
        );
        setLoading(false);

        // Hero im Hintergrund nachladen und einblenden
        const hero = await loadCaptureCalendarHero({ userLogin: login, year: viewYear });
        if (requestId !== requestIdRef.current) return;
        if (!hero) {
          setHeroReady(false);
          return;
        }

        setMonthGrid(
          buildMonthGrid(
            viewYear,
            viewMonth,
            appHoursRef.current,
            absenceByDayRef.current,
            calendarByDayRef.current,
            {
              hoursByDay: hero.hoursByDay,
              absenceByDay: hero.absenceByDay,
            },
            undefined,
            undefined,
            pendingByDayRef.current,
          ),
        );
        setHeroReady(true);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error(
          err instanceof ApiError ? err.message : "Monatskalender konnte nicht geladen werden.",
        );
        setMonthGrid(
          buildMonthGrid(viewYear, viewMonth, new Map(), new Map(), new Map()),
        );
        setLoading(false);
      }
    })();
  }, [userLogin, viewYear, viewMonth, refreshKey]);

  const today = todayIso();
  const now = new Date();
  const isCurrentMonth =
    viewYear === now.getFullYear() && viewMonth === now.getMonth() + 1;

  const goMonth = (delta: number) => {
    const next = shiftMonth(viewYear, viewMonth, delta);
    setViewYear(next.year);
    setViewMonth(next.month);
  };

  const handleSelect = (cell: YearGridCell) => {
    onSelectDate(cell.iso);
  };

  const appTotal = monthGrid?.appTotal ?? 0;
  const heroTotal = monthGrid?.heroTotal ?? 0;
  const monthTotal = monthGrid?.total ?? 0;
  const hasAppHours = appTotal > 0;
  const hasHeroHours = heroReady && heroTotal > 0;
  const showSourceSplit = hasAppHours && hasHeroHours;
  const showHeroOnTotal = hasHeroHours && !hasAppHours;

  if (!userLogin.trim()) {
    return (
      <section className="year-month-card year-month-card--capture">
        <header className="year-month-card__header">
          <h3 className="year-month-card__name">Monat</h3>
        </header>
        <p className="px-1 pb-1 text-sm text-label-secondary">
          Mitarbeiter wählen, um den Monatskalender zu sehen.
        </p>
      </section>
    );
  }

  return (
    <section
      className={[
        "year-month-card",
        "year-month-card--capture",
        isCurrentMonth ? "year-month-card--current" : "",
        heroReady ? "year-month-card--hero-ready" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="year-month-card__header">
        <div className="flex min-w-0 items-center gap-1">
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-lg text-label-secondary transition hover:bg-fill-tertiary hover:text-label"
            aria-label="Vorheriger Monat"
            onClick={() => goMonth(-1)}
          >
            ‹
          </button>
          <h3 className="year-month-card__name min-w-0 truncate">
            {MONTH_LONG[viewMonth - 1]} {viewYear}
          </h3>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-lg text-label-secondary transition hover:bg-fill-tertiary hover:text-label"
            aria-label="Nächster Monat"
            onClick={() => goMonth(1)}
          >
            ›
          </button>
        </div>
        <div className="year-month-card__totals">
          <span
            className={[
              "year-month-card__total",
              monthTotal > 0 ? "year-month-card__total--active" : "",
              showHeroOnTotal ? "year-month-card__total--with-hero" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {showHeroOnTotal ? <HeroInlineLogo /> : null}
            {monthTotal > 0 ? formatHours(monthTotal) : "—"}
          </span>
          {showSourceSplit ? (
            <span className="year-month-card__hero-hint year-day__hero-fade-in">
              <span>{formatHours(appTotal)}</span>
              <span className="year-month-card__hero-split">+</span>
              <span className="inline-flex items-center gap-0.5">
                <HeroInlineLogo />
                {formatHours(heroTotal)}
              </span>
            </span>
          ) : null}
        </div>
      </header>

      {loading && !monthGrid ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : monthGrid ? (
        <>
          <div className="year-month-card__weekdays" aria-hidden>
            {WEEKDAY_SHORT.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="year-month-card__grid">
            {monthGrid.cells.map((cell, idx) =>
              cell ? (
                <YearOverviewCell
                  key={cell.iso}
                  cell={cell}
                  isToday={cell.iso === today}
                  isSelected={cell.iso === selectedDate}
                  onSelect={handleSelect}
                />
              ) : (
                <span key={`blank-${idx}`} className="year-day year-day--blank" aria-hidden />
              ),
            )}
          </div>
          <p className="mt-2 px-0.5 text-xs text-label-tertiary">
            Tag anklicken, um Zeiten zu laden und zu bearbeiten.
            {pendingJustificationCount > 0 ? (
              <span className="ml-1 text-status-orange">
                Orange markierte Tage zählen erst nach Begründung.
              </span>
            ) : null}
            {!heroReady && (
              <span className="ml-1 opacity-70">Hero-Zeiten werden geladen…</span>
            )}
          </p>
        </>
      ) : null}
    </section>
  );
}
